import React, {ReactNode, useCallback, useRef, useState} from 'react';
import intl from "react-intl-universal";
import {observer} from "mobx-react-lite";
import classNames from "classnames";
import {useAppState, useDsManager} from "@stores/AppStateContext";

import {CheckoutContentProps, PaymentGateway} from "../../types";

import {SuccessIcon} from "../../../ui/components/icons/SuccessIcon";
import {TextButton} from "../../../ui/components/TextButton";
import {Callout} from "../../../ui/components/Callout";
import {ModalBase} from "../../../ui/screens/modals/components/ModalBase";
import {SelectPeriod} from "./components/SelectPeriod";
import {Input} from "../../../ui/components/form/Input";
import {PriceDetails} from "./components/PriceDetails";
import {HintRow} from "../../../ui/components/HintRow";
import {SeatManager} from "../../../services/SeatManager";
import {PersonIcon} from "../../../ui/components/icons/PersonIcon";
import {Tooltip} from "../../../ui/components/Tooltip";
import {HelpIcon} from "../../../ui/components/icons/HelpIcon";
import {Button} from "../../../ui/components/Button";
import {InfoIcon} from "../../../ui/components/icons/InfoIcon";
import {outputPriceWithCurrency} from "../../../utils/outputPriceWithCurrency";

import {LoadingCircle} from "../../../ui/components/LoadingCircle";
import {SuccessModal} from "../base/SuccessModal";

import './BaseCheckout.css';

export interface BaseCheckoutProps extends BaseCheckoutContentProps {
    header?: string;
}

export const BaseCheckout = (
    {
        header = intl.get('checkout.title').d('Upgrade plan'),
        ...props
    }: BaseCheckoutProps
) => {
    const appState = useAppState();
    const handleClose = useCallback(() => {
        appState.setModal(null);
    }, []);

    return (
        <ModalBase
            open={true}
            header={header}
            onClose={handleClose}
            maxHeight={896}
            minWidth={484}
            headerClass={'headline-medium-bold'}
        >
            <BaseCheckoutContent {...props}/>
        </ModalBase>
    )
};

export interface BaseCheckoutContentProps extends CheckoutContentProps {
    paymentStep?: ReactNode;
    vertical?: boolean;
    continueButtonLabel?: string;
}

const BaseCheckoutContent = observer<BaseCheckoutContentProps>((
        {
            paymentGateway,
            paymentStep,
            vertical = false,
            continueButtonLabel,
        },
    ) => {
        const appState = useAppState();
        const dsManager = useDsManager();
        const [step, setStep] = useState(1);
        const frameRootRef = useRef<HTMLDivElement | null>(null);
        const currentClient = dsManager!.clientAR!.row!;
        const planConfig = SeatManager.getPlanConfig(currentClient.subscription_plan);
        const discountMessage = paymentGateway.checkoutInput.period === 'yearly'
            ? intl.get('checkout.discount_yearly_message').d('–50% for the first year')
            : intl.get('checkout.discount_monthly_message').d('–90% for your first 3 months');

        const handleContinue = useCallback(async () => {
            if (paymentGateway.isUpgradeFlow) {
                await paymentGateway.handleUpgrade();
                return;
            }
            if (paymentStep) {
                setStep(2);
            }
        }, [paymentStep]);

        if (paymentGateway.showSuccessPayment) {
            if (paymentGateway.priceCurrency === 'USD') {
                return (
                    <SuccessModal
                        titleText={intl.get('common.thank_you').d('Thank you!')}
                        text={intl.get('BaseCheckout-success-msg').d('The payment was successful')}
                    />
                )
            } else {
                return (
                    <SuccessModal
                        titleText={intl.get('common.thank_you').d('Thank you!')}
                        text={intl.get('BaseCheckout-invoice-success-msg').d('Upgraded successfully - you can now use the new limits.')}
                    />
                )
            }
        }

        const renderStep = () => {
            if (step === 1) {
                return (
                    <div className="BaseCheckout_settings">
                        <div className="BaseCheckout_setting-line">
                            <div className="BaseCheckout_setting-label body-large-bold">
                                {intl.get('checkout.period-select').d('Billing period')}
                            </div>
                            <div className="BaseCheckout_setting-value">
                                <SelectPeriod paymentGateway={paymentGateway}/>
                            </div>
                        </div>
                        <div className="BaseCheckout_setting-line">
                            <div className="BaseCheckout_setting-label body-large-bold">
                                {intl.get('checkout.location_slots').d('Location slots')}
                                <Tooltip
                                    title={intl.get('checkout.location_slots_tooltip', {userPrice: '$3/month'}).d('5 users included per location, each additional is {userPrice}')}>
                                    <HelpIcon size={20} className="SubscriptionUpgradeCheckout_help-icon"/>
                                </Tooltip>
                            </div>
                            <div className="BaseCheckout_setting-value">
                                <Input
                                    type="number"
                                    value={paymentGateway.checkoutInput.locationSlotsCount}
                                    onChange={(value) => {
                                        paymentGateway.setCheckoutInput({
                                            locationSlotsCount: parseInt(value)
                                        });
                                    }}
                                />
                            </div>
                        </div>
                        <div className="BaseCheckout_setting-line">
                            <div className="BaseCheckout_setting-label body-large-bold">
                                {intl.get('checkout.user_seats').d('Users seats')}
                            </div>
                            <div className="BaseCheckout_setting-value">
                                <Input
                                    type="number"
                                    value={paymentGateway.checkoutInput.userSeatsCount}
                                    onChange={(value) => {
                                        paymentGateway.setCheckoutInput({
                                            userSeatsCount: parseInt(value)
                                        });
                                    }}
                                />
                            </div>
                            <HintRow
                                className="BaseCheckout_setting-value"
                                text={intl.get('checkout.user_seats.included').d('Included with locations:')}
                                number={paymentGateway.checkoutInput.locationSlotsCount * planConfig.usersPerLocation}
                                Icon={PersonIcon}
                            />
                            <HintRow
                                className="BaseCheckout_setting-value"
                                text={intl.get('checkout.user_seats.billable').d('Extra users billed:')}
                                number={paymentGateway.billableUserSeats}
                                Icon={PersonIcon}
                            />
                        </div>
                        {paymentGateway.validationErrors.length > 0
                            ? (
                                paymentGateway.validationErrors.map(error => (
                                    <Callout
                                        className={'PageSurface__width'}
                                        type={'danger'}
                                        text={error.message}
                                    />
                                ))
                            )
                            : (
                                paymentGateway.isUpgradeFlow
                                    ? (
                                        <UpgradeAdditionSection paymentGateway={paymentGateway}/>
                                    )
                                    : (
                                        <div className="BaseCheckout_setting-total-block">
                                            {paymentGateway.hasDiscountIntent
                                                ? (
                                                    <Callout
                                                        Icon={SuccessIcon}
                                                        className="BaseCheckout__discount-callout PageSurface__width"
                                                        type="success"
                                                        text={<div>
                                                            <div>{intl.get('checkout.discount_monthly_title').d('Early user discount applied.')}</div>
                                                            <b>{discountMessage}</b>
                                                        </div>}
                                                    />
                                                )
                                                : null
                                            }
                                            <div className="BaseCheckout_setting-label body-large-bold">
                                                {intl.get('checkout.total-label').d('Total')}
                                            </div>
                                            <div className="BaseCheckout_setting-value">
                                                <PriceDetails paymentGateway={paymentGateway}
                                                              isUpgrade={paymentGateway.isUpgradeFlow}
                                                />
                                            </div>
                                        </div>
                                    )
                            )
                        }
                        <div className="BaseCheckout__btns-row">
                            <Tooltip
                                title={intl.get('checkout.location_slots_tooltip', {userPrice: '$3/month'}).d('5 users included per location, each additional is {userPrice}')}
                            >
                                <TextButton
                                    onClick={() => {
                                        window.open('/help', '_blank');
                                    }}
                                    text={intl.get('checkout.learn_more').d('Learn more')}
                                    Icon={InfoIcon}
                                    iconSize={16}
                                />
                            </Tooltip>
                            <div className="BaseCheckout__btns">
                                <Button
                                    style="plain"
                                    onClick={() => appState.setModal(null)}
                                    text={intl.get('common.cancel').d('Cancel')}
                                />
                                <Button
                                    style="solid"
                                    onClick={handleContinue}
                                    loading={paymentGateway.isProcessingUpgrade}
                                    isDisable={!paymentGateway.canProceed
                                        || (paymentGateway.isUpgradeFlow && !paymentGateway.hasChanges)
                                        || paymentGateway.isProrationLoading
                                        || paymentGateway.isProcessingUpgrade
                                    }
                                    text={continueButtonLabel ?? intl.get('checkout.continue_to_payment').d('Continue')}
                                />
                            </div>
                        </div>
                    </div>
                )
            } else {
                return (
                    paymentStep
                );
            }
        }

        return (
            <div
                ref={frameRootRef}
                className={classNames({
                    "BaseCheckout": true,
                    "BaseCheckout-vertical": vertical,
                })}
            >
                {renderStep()}
            </div>
        )
    }
);

interface UpgradeAdditionSectionProps {
    paymentGateway: PaymentGateway;
}

const UpgradeAdditionSection = (
    {
        paymentGateway
    }: UpgradeAdditionSectionProps
) => {
    if (paymentGateway.isProrationLoading) {
        return (
            <LoadingCircle/>
        )
    } else if (paymentGateway.prorationError) {
        return (
            <Callout
                className={'PageSurface__width'}
                type={'danger'}
                text={intl.get('checkout.proration.error').d('We could not calculate the prorated amount. Please try again later.')}
            />
        )
    } else if (paymentGateway.prorationDueNow) {
        return <>
            <PriceDetails paymentGateway={paymentGateway}
                          isUpgrade={true}
            />
            <Callout
                className={'PageSurface__width'}
                type={'info'}
                text={(
                    paymentGateway.priceCurrency === 'USD'
                        ? (
                            intl.get('checkout.proration.due_now', {
                                price: outputPriceWithCurrency(
                                    paymentGateway.prorationDueNow.totals.total,
                                    paymentGateway.prorationDueNow.currency
                                ),
                            }).d(`You will be charged a pro-rated fee of {price} (incl. VAT)`)
                        )
                        : (
                            intl.get('checkout.proration.with_next_payment', {
                                price: outputPriceWithCurrency(
                                    paymentGateway.prorationDueNow.totals.total,
                                    paymentGateway.prorationDueNow.currency
                                ),
                            }).d(`A pro-rated fee of {price} (incl. VAT) will be added to the next invoice`)
                        )

                )}
            />
        </>
    }
    return null;
}
